import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { apiFetch } from "../lib/api";

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hello - I'm the Kashmir Concierge. Ask me about any shawl, material or how to pick a piece.",
    },
  ]);

  const [models, setModels] = useState([]);
  const [model, setModel] = useState("");
  const [session] = useState(() => "chat-" + Math.random().toString(36).slice(2, 10));

  const bodyRef = useRef(null);

  useEffect(() => {
    apiFetch("/ai/models")
      .then((items) => {
        setModels(items);
        setModel((current) => current || items[0]?.id || "");
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTo(0, bodyRef.current.scrollHeight);
    }
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();

    const text = input.trim();

    if (!text || sending) return;

    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);

    try {
      const data = await apiFetch("/ai/chat", {
        method: "POST",
        body: JSON.stringify({
          session_id: session,
          message: text,
          model,
        }),
      });

      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: data.reply || "...",
        },
      ]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: "Sorry - the concierge is briefly unavailable. Try again in a moment.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        className={`chat-fab ${open ? "open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        data-testid="chatbot-toggle"
        aria-label="Open concierge"
      >
        {open ? <X size={20} /> : <MessageCircle size={22} />}
      </button>

      {open && (
        <div
          className="chat-panel"
          role="dialog"
          aria-label="Kashmir Concierge"
          data-testid="chatbot-panel"
        >
          <div className="chat-head">
            <div>
              <span className="eyebrow">
                <Sparkles size={12} /> Kashmir Concierge
              </span>
              <h4>Here to help you choose.</h4>
            </div>

            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="chat-model"
              aria-label="AI model"
              data-testid="chatbot-model-select"
            >
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          <div className="chat-body" ref={bodyRef}>
            {messages.map((m, i) => (
              <div
                key={i}
                className={`chat-msg ${m.role}`}
                data-testid={`chat-msg-${m.role}-${i}`}
              >
                {m.text}
              </div>
            ))}

            {sending && (
              <div className="chat-msg assistant typing">...</div>
            )}
          </div>

          <form className="chat-input" onSubmit={send}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask the concierge..."
              aria-label="Message"
              data-testid="chatbot-input"
            />

            <button
              type="submit"
              disabled={sending}
              data-testid="chatbot-send"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
